import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import Layers3Icon from "lucide-react/dist/esm/icons/layers-3";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@quake/shared-ui/components/ui/dialog";
import { Input } from "@quake/shared-ui/components/ui/input";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { trpc } from "~/utils/trpc";

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/skills/sets/")({
  component: SkillSetsIndexRouteComponent,
});

function SkillSetsIndexRouteComponent() {
  const { datasetSlug } = Route.useParams();
  useBreadCrumbs([
    { title: "Skills", link: `/${datasetSlug}/data/skills` },
    { title: "Skill sets", link: `/${datasetSlug}/data/skills/sets` },
  ]);
  return <SkillSetsIndexPage datasetSlug={datasetSlug} />;
}

export function SkillSetsIndexPage({ datasetSlug }: { datasetSlug: string }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const presetsQuery = useQuery(
    trpc.skills.listPresets.queryOptions(
      { pagination: { page, pageSize: 24 } },
      { placeholderData: (previousData) => previousData },
    ),
  );
  const createPreset = useMutation(
    trpc.skills.createPreset.mutationOptions({
      onSuccess: invalidateSkillQueries(queryClient),
    }),
  );
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");

  if (presetsQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading skill sets…</div>;
  }

  const presetsPage = presetsQuery.data;
  if (!presetsPage) {
    return <div className="p-6 text-sm text-muted-foreground">Skill sets unavailable.</div>;
  }

  const handleCreate = async () => {
    if (!name.trim()) return;
    await createPreset.mutateAsync({
      name: name.trim(),
      purpose: purpose.trim() ? purpose : null,
    });
    setName("");
    setPurpose("");
    setOpen(false);
  };

  return (
    <div className="min-h-[calc(100vh-4.5rem)]">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-6 py-6">
        <header>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl">
              <h1 className="text-2xl font-bold tracking-tight">Skill sets</h1>
              <p className="mt-1 text-sm text-muted-foreground">Group skills for reuse across recruiter workflows.</p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {presetsPage.total} set{presetsPage.total === 1 ? "" : "s"}
              </span>
              <Button onClick={() => setOpen(true)}>
                <PlusIcon className="mr-2 size-4" />
                New skill set
              </Button>
            </div>
          </div>
        </header>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create skill set</DialogTitle>
            </DialogHeader>
            <div className="mt-2 grid gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Name</label>
                <Input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Frontend core"
                  className="mt-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Purpose</label>
                <textarea
                  className="mt-2 min-h-24 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500 dark:border-white/10 dark:bg-slate-950"
                  value={purpose}
                  onChange={(event) => setPurpose(event.target.value)}
                  placeholder="Explain when recruiters should use this set."
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate} disabled={!name.trim() || createPreset.isPending}>
                  <PlusIcon className="mr-2 size-4" />
                  Create set
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {presetsPage.data.map((preset) => (
            <Link
              key={preset.id}
              to="/$datasetSlug/data/skills/sets/$set_id"
              params={{ datasetSlug, set_id: preset.id }}
              className="group rounded-xl border bg-background p-4 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold">{preset.name}</p>
                  <p className="mt-1 line-clamp-3 text-sm leading-5 text-muted-foreground">
                    {preset.purpose ?? "No purpose added yet."}
                  </p>
                </div>
                <Layers3Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              </div>
              <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {preset.member_count} skill{preset.member_count === 1 ? "" : "s"}
                </span>
                <span>{preset.mode.replace("_", " ")}</span>
              </div>
            </Link>
          ))}

          {presetsPage.data.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">
              No skill sets yet. Create one above to start grouping skills.
            </div>
          )}
        </section>

        {presetsPage.total > presetsPage.pageSize && (
          <div className="flex items-center justify-between rounded-xl border bg-background px-4 py-3 text-sm text-muted-foreground">
            <span>
              Page {presetsPage.page} of {Math.max(presetsPage.totalPages, 1)}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= presetsPage.totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function invalidateSkillQueries(queryClient: ReturnType<typeof useQueryClient>) {
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries(trpc.skills.listPresets.queryFilter()),
      queryClient.invalidateQueries(trpc.skills.getCatalogMeta.queryFilter()),
    ]);
  };
}
